import { SingleOrderAuthError } from "@redotech/customer-portal-sdk/rpc/schema/single-order-auth";
import { useRequiredContext } from "@redotech/react-util/context";
import { LoadState, useTriggerLoad } from "@redotech/react-util/load";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { Step } from "@redotech/redo-model/return-flow";
import { ReturnOptionMethod } from "@redotech/redo-model/return-flow/return-option";
import { ClientError } from "@redotech/rpc/errors";
import { useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SetAuthContext } from "../contexts/auth";
import { SettingsContext } from "../contexts/settings";
import { SetUserContext } from "../contexts/user";
import {
  getHeaderText,
  singleOrderAuthErrorToText,
} from "./dynamic-text-utils";

export interface UseSingleOrderLoginResponse {
  texts: {
    title: string;
    subtitle: string;
    emailZipInputLabel: string;
    emailZipInputPlaceholder: string;
    orderNumberInputLabel: string;
    orderNumberInputPlaceholder: string;
    startReturnButton: string;
  };

  emailZip: string;
  orderTrackingNumber: string;
  setEmailZip: (emailZip: string) => void;
  setOrderTrackingNumber: (orderTrackingNumber: string) => void;
  submit: () => void;
  submitLoad: LoadState<void | undefined>;
  submitDisabled: boolean;
}
export function useSingleOrderLogin(): UseSingleOrderLoginResponse {
  const settings = useRequiredContext(SettingsContext);
  const rpcClient = useContext(RpcClientContext);
  const setAuth_ = useContext(SetAuthContext);
  const setUser = useContext(SetUserContext);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [emailZip, setEmailZip] = useState<string>("");
  const [orderTrackingNumber, setOrderTrackingNumber] = useState<string>("");

  const isCreateGiftReturnLink =
    params.get("create_gift_return_link") !== null &&
    !!settings?.returns?.enableGiftReceipts;
  const nextPath = params.get("next");
  const [submitLoad, triggerSubmitLoad] = useTriggerLoad(async () => {
    if (
      !emailZip ||
      !orderTrackingNumber ||
      !rpcClient ||
      !setAuth_ ||
      !setUser
    ) {
      return;
    }

    try {
      const result = await rpcClient.singleOrderAuth({
        emailOrZip: emailZip,
        orderIdTrackingNumber: orderTrackingNumber,
      });

      if (result.customerAuthToken && result.orderId) {
        setAuth_(result.customerAuthToken);
        setUser(result.customer ?? undefined);

        //We need to navigate to the gift receipt page if the user is coming from a gift return link
        if (isCreateGiftReturnLink) {
          navigate(
            `/returns-portal/orders/${result.orderId}/gift-receipts/router`,
            { replace: true },
          );
          return;
        }

        if (nextPath && nextPath.startsWith("/returns-portal/")) {
          const resolvedPath = nextPath.replace("{orderId}", result.orderId);
          navigate(resolvedPath, { replace: true });
          return;
        }

        navigate(`/returns-portal/orders/${result.orderId}`, { replace: true });
      } else {
        throw new Error("Something went wrong on our end, please try again.");
      }
    } catch (e) {
      if (e instanceof ClientError) {
        const rpcError = e as ClientError<SingleOrderAuthError>;
        if (rpcError.code) {
          throw new Error(singleOrderAuthErrorToText[rpcError.code]);
        }
      }
      throw e;
    }
  });

  // If the merchant only offers returns for refund then we should hide the option for returning a gift,
  // since gift returns cannot be returned for refund.
  let hideGiftOption = true;
  if (
    settings?.packageProtection?.enabled &&
    settings.packageProtection.customerClaimsEnabled &&
    !settings.hideGiftOption
  ) {
    hideGiftOption = false;
  }
  if (settings?.returns?.enabled && hideGiftOption) {
    const returnOptionSteps = settings.returnFlow.steps.filter(
      (step) => step.type === Step.RETURN,
    );
    for (const returnOptionStep of returnOptionSteps) {
      for (const option of returnOptionStep.options) {
        if (option.method.type !== ReturnOptionMethod.REFUND) {
          hideGiftOption = false;
        }
      }
    }
  }

  return {
    texts: {
      title: getHeaderText(settings),
      subtitle:
        settings?.loginSubtitleText ||
        "Enter your email or zip and order or tracking number to login",
      emailZipInputLabel: settings?.emailLoginLabelText || "Email or Zip code",
      emailZipInputPlaceholder: settings?.emailLoginLabelText
        ? `Enter ${settings.emailLoginLabelText.toLowerCase()}...`
        : "Enter email or zip...",
      orderNumberInputLabel:
        settings?.orderNumberLoginLabelText || "Order # or Tracking #",
      orderNumberInputPlaceholder: settings?.orderNumberLoginLabelText
        ? `Enter ${settings.orderNumberLoginLabelText.toLowerCase()}...`
        : "Enter order or tracking #...",
      startReturnButton: settings?.returnPortalButtonText || "Search",
    },
    emailZip: emailZip || "",
    orderTrackingNumber: orderTrackingNumber || "",
    setEmailZip,
    setOrderTrackingNumber,
    submit: triggerSubmitLoad,
    submitLoad,
    submitDisabled: !emailZip || !orderTrackingNumber || submitLoad.pending,
  };
}
